"use client";
import {
  BodyText,
  DetailText,
  LinkCard,
  Heading2Text,
} from "@gnist/design-system";
import React, { FC } from "react";
import NextLink from "../nextLink";
import { styled } from "styled-components";
import defaultThumbnail from "@/public/images/defaultCar-thumbnail.jpg";
import CarProperties from "./carProperties";
import CarThumbnailImage from "./carThumbnailImage";
import DepartmentSection from "./departmentSection";
import { Car } from "@/data/bruktbilsalgBackend/types/car.types";
import { numberWithSpaces } from "@/utils/numberWithSpacing";
import { CampaignBadge } from "../campaign/campaignBadge";
import { useTradeInContext } from "@/components/tradeIn/tradeInProvider";
import { useDealerCampaign } from "../campaign/campaignProvider";
import { getDepartmentNameById } from "@/data/forhandler/departments";
import { tokens } from "@gnist/themes/tokens.css.js";
import { getSortedCarImages } from "@/utils/getSortedCarImages";
import {
  calculateEffectiveAnnualInterestRate,
  calculateMonthlyPayment,
  ESTABLISHMENT_FEE,
  MONTHLY_FEE,
  NOMINAL_ANNUAL_INTEREST_RATE,
} from "../loanCalculator/loanCalculatorShared";

interface Props {
  car: Car;
  departmentSlug?: string;
  isEmbedded?: boolean;
}

const CardContent = styled.div`
  padding: ${tokens.spacing.xs} ${tokens.spacing.xs} 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1;
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  flex: 1;

  h2 {
    font-size: ${tokens.typeface.size.xxl};
  }
`;

const StyledCard = styled.div`
  display: flex;
  width: 100%;

  a {
    width: 100%;
    padding: 0 0 ${tokens.spacing.s};
    overflow: hidden;
    border-radius: ${tokens.radius.card};
    background-color: ${tokens.color["surface-variant"]};
    color: ${tokens.color["on-surface"]};
    height: 100%;
    display: flex;
    flex-direction: column;
  }
`;

const BottomGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacing.xs};
`;

const BottomContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Price = styled(BodyText)`
  font-size: ${tokens.typeface.size.xxl};
  font-weight: ${tokens.typeface.weight.semibold};
  text-align: end;
`;

const MinifiedTotalPrice = styled(DetailText)`
  font-size: ${tokens.typeface.size.s};
`;

const Prefix = styled.span`
  font-size: ${tokens.typeface.size.base};
`;

const YourPrice = styled.span`
  font-size: ${tokens.typeface.size.l};
  font-weight: ${tokens.typeface.weight.semibold};
`;

const SpaceBetweenContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
`;

const SmallerText = styled.span`
  font-size: ${tokens.typeface.size.s};
`;

const FullWidth = styled.div`
  width: 100%;
`;

export const CarCard: FC<Props> = ({
  car,
  departmentSlug,
  isEmbedded = false,
}) => {
  const sortedImages = car.carImages ? getSortedCarImages(car.carImages) : [];
  const thumbnailImage =
    sortedImages.length > 0
      ? sortedImages[0].framedUrl ||
        sortedImages[0].normalUrl ||
        sortedImages[0].thumbnailUrl
      : defaultThumbnail;

  const departmentName =
    getDepartmentNameById(car.departmentId) ?? car.departmentName;

  const handleClick = (e: React.MouseEvent) => {
    if (isEmbedded) {
      e.preventDefault();
      window.parent.postMessage({ type: "carClicked", carId: car.carId }, "*");
    }
  };

  return (
    <StyledCard>
      <LinkCard
        aria-label={`${car.brand} ${car.model} (${car.modelYear}), ${car.isLeasing && car.simpleCalcMonthlyPaymentInclVat ? `${car.simpleCalcMonthlyPaymentInclVat} kr per måned` : `${car.price}kr`}, ${car.mileage}km, ${car.fuel}. Selges hos ${departmentName}`}
        $as={NextLink}
        href={
          departmentSlug
            ? `/forhandler/${departmentSlug}/bil/${car.carId}`
            : `/bil/${car.carId}`
        }
        onClick={handleClick}
      >
        <CarThumbnailImage
          thumbnailImageUrl={thumbnailImage}
          isSold={car.isSold}
          availableFromDate={car.availableFromDate}
        />
        <CardContent>
          <TitleGroup>
            <DepartmentSection departmentName={departmentName} />
            <TitleWrapper>
              <Heading2Text $as="h2">
                {car.brand} {car.model}
              </Heading2Text>
            </TitleWrapper>
          </TitleGroup>
          <BottomGroup>
            <CarProperties car={car} />
            <BottomContent>
              <PriceComponent car={car} departmentSlug={departmentSlug} />
            </BottomContent>
          </BottomGroup>
        </CardContent>
      </LinkCard>
    </StyledCard>
  );
};

const PriceComponent: React.FC<{ car: Car; departmentSlug?: string }> = ({
  car,
  departmentSlug,
}) => {
  const { dealerCampaign } = useDealerCampaign(car.departmentName);
  const { isTradeInPricingOn, tradeInPrice } = useTradeInContext();
  const showTradeInPrice = !departmentSlug && isTradeInPricingOn;
  const totalPriceString = `${numberWithSpaces(car.price)} kr`;
  const updatedPriceString = `${numberWithSpaces(car.price - tradeInPrice)} kr`;
  const isMollerBil = departmentSlug === "moller-bil";

  const getPriceText = () => {
    if (car.isLeasing) {
      return car.simpleCalcMonthlyPaymentInclVat
        ? `${numberWithSpaces(car.simpleCalcMonthlyPaymentInclVat)} kr/mnd`
        : "Leasing";
    }
    if (car.price === 0) {
      return "Kontakt oss for pris";
    }
    if (showTradeInPrice) {
      return updatedPriceString;
    }
    return totalPriceString;
  };
  /*
  if (!showTradeInPrice && !car.isLeasing && car.price > 0 && isMollerBil)
    return (
      <FullWidth>
        <MollerBilPrice fullPrice={car.price} />
        {dealerCampaign && (
          <CampaignBadge dealerGroupName={departmentSlug || ""} />
        )}
      </FullWidth>
    );
  */
  return (
    <>
      <div>
        {showTradeInPrice && (
          <MinifiedTotalPrice>{`Totalpris: ${totalPriceString}`}</MinifiedTotalPrice>
        )}

        <Price>
          {showTradeInPrice && <Prefix>Din pris </Prefix>}
          {getPriceText()}
        </Price>
      </div>
      {dealerCampaign && (
        <CampaignBadge dealerGroupName={departmentSlug || ""} />
      )}
    </>
  );
};

const MollerBilPrice: React.FC<{ fullPrice: number }> = ({
  fullPrice,
}) => {
  const downPaymentFraction = 0.1;
  const innbytte = Math.round(fullPrice * downPaymentFraction);
  const tradeInPrice = fullPrice - innbytte;
  const numberOfYears = 7;

  const monthly = calculateMonthlyPayment({
    nominalAnnualInterestRate: NOMINAL_ANNUAL_INTEREST_RATE,
    establishmentFee: ESTABLISHMENT_FEE,
    monthlyFee: MONTHLY_FEE,
    loanAmount: tradeInPrice,
    years: numberOfYears,
  });

  const effectiveInterestRate = calculateEffectiveAnnualInterestRate({
    loanAmount: tradeInPrice,
    monthlyPayment: monthly,
    years: numberOfYears,
  });

  return (
    <>
      <SpaceBetweenContainer>
        <YourPrice>{numberWithSpaces(fullPrice)} kr</YourPrice>
        <span>{numberWithSpaces(Math.round(monthly))} kr/mnd</span>
      </SpaceBetweenContainer>
      <div>
        <SmallerText>
          Finansiering: {downPaymentFraction * 100}% egenkapital{" "}
          {Math.round(effectiveInterestRate! * 10000) / 100}% rente
        </SmallerText>
      </div>
    </>
  );
};
